bcl.ariaUtils = {
  _selectors: {
    titleElements: 'h1, h2, h3, h4, h5, h6, legend, caption, summary, [role="heading"]',
    focusableElements: `
      a[href],
      area[href], 
      button:not([disabled]),
      input:not([type="hidden"]):not([disabled]),
      select:not([disabled]),
      textarea:not([disabled]),
      [tabindex], 
      [role="button"], 
      [role="link"], 
      [role="checkbox"], 
      [role="radio"], 
      [role="switch"], 
      [role="menuitem"], 
      [role="option"],
      [contenteditable="true"], 
      [role="dialog"], 
      [role="tab"]
    `,
  },
  _hiddenClasses: [".hidden", ".visuallyhidden", ".mod--hidden", "mod--hide"],

  _activeTrapContainer: null,
  _boundTrapFocusHandler: null,
  _uniqueIdCounter: 0,
  /**
   * Generates a unique ID for ARIA elements if they don't have one.
   * Internal to bcl.ariaUtils.
   * @param {string} [prefix='auto-aria-id-'] - Prefix for the generated ID.
   * @returns {string} A unique ID.
   */
  _generateUniqueId(prefix = "auto-aria-id-") {
    return `${prefix}${this._uniqueIdCounter++}`;
  },

  /**
   * Gets the first title element within a given container.
   * @param {HTMLElement} container - The container element to search within.
   * @returns {HTMLElement|null} - The first found title element or null.
   */
  getFirstTitleElement(container) {
    if (!(container instanceof HTMLElement)) {
      return null;
    }
    return container.querySelector(this._selectors.titleElements);
  },

  /**
   * Gets all focusable elements within a container.
   * @param {HTMLElement} container - The container element to search within.
   * @param {boolean} [includeNegativeTabIndex=false] - If true, includes elements with tabindex="-1".
   * @returns {HTMLElement[]} - An array of focusable elements.
   */
  getFocusableElements(container, includeNegativeTabIndex = false) {
    if (!(container instanceof HTMLElement)) {
      return [];
    }

    const initialCandidates = Array.from(container.querySelectorAll(this._selectors.focusableElements));

    if (container.tabIndex === 0 && !initialCandidates.includes(container)) {
      initialCandidates.unshift(container);
    }
    return initialCandidates.filter((el) => {
      if (el.closest(this._hiddenClasses.join(", "))) {
        return false;
      }
      if (!bcl.u.mobile.isMobile() && el.closest(".md\\:hidden")) {
        return false;
      }

      if (!includeNegativeTabIndex && el.tabIndex === -1) {
        return false;
      }

      if ((el.hasAttribute("disabled") && el.getAttribute("disabled") !== "false") || (el.hasAttribute("aria-disabled") && el.getAttribute("aria-disabled") === "true") || (el.hasAttribute("aria-hidden") && el.getAttribute("aria-hidden") === "true")) {
        return el?.tabIndex >= 0;
      }
      return true;
    });
  },
  /**
   * Deactivates the active focus trap in the previously activated container.
   * If there is no active trap or the container does not match, does nothing.
   *
   * @param {HTMLElement} [containerToDeactivate=null] - Optional. If provided, only deactivates if it is this container.
   */
  deactivateFocusTrap(containerToDeactivate = null) {
    if (!this._activeTrapContainer || !this._boundTrapFocusHandler) {
      return;
    }
    if (containerToDeactivate && this._activeTrapContainer !== containerToDeactivate) {
      return;
    }

    this._activeTrapContainer.removeEventListener("keydown", this._boundTrapFocusHandler);

    this._activeTrapContainer = null;
    this._boundTrapFocusHandler = null;
  },
  /**
   * Activates the focus trap within a specified container (usually a modal or dialog).
   * Moves focus to the first focusable element and manages Tab navigation.
   *
   * @param {HTMLElement} container - The container element (e.g., the modal) where the focus trap will be applied.
   * @param {HTMLElement} [initialFocusElement=null] - An optional element to receive initial focus.
   * If null, focus goes to the first focusable element.
   */
  activateFocusTrap(container, initialFocusElement = null) {
    if (!(container instanceof HTMLElement)) {
      return;
    }
    if (this._activeTrapContainer === container) {
      return;
    }
    if (this._activeTrapContainer) {
      this.deactivateFocusTrap();
    }

    const focusableElements = this.getFocusableElements(container);

    if (focusableElements.length === 0) {
      if (container.tabIndex === 0) {
        container.focus();
      }
      return;
    }

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    const targetFocusElement = initialFocusElement && focusableElements.includes(initialFocusElement) ? initialFocusElement : firstFocusableElement;

    targetFocusElement.focus();

    this._boundTrapFocusHandler = (event) => {
      if (event.key === "Tab") {
        if (event.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            event.preventDefault();
            lastFocusableElement.focus();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            event.preventDefault();
            firstFocusableElement.focus();
          }
        }
      }
    };

    container.addEventListener("keydown", this._boundTrapFocusHandler);

    this._activeTrapContainer = container;
  },
  /**
   * Sets up the basic ARIA structure for a modal or dialog.
   * Assigns role="dialog", aria-modal="true", and aria-labelledby to the modal.
   * If the title element does not have an ID, assigns a unique one.
   *
   * @param {HTMLElement} modalBoxContent - The main DOM element of the modal content.
   * This element MUST be the actual container of the modal (e.g., the div with the content).
   */
  buildModalHtmlStructure(modalBoxContent) {
    if (!(modalBoxContent instanceof HTMLElement)) {
      return;
    }

    modalBoxContent.setAttribute("role", "dialog");
    modalBoxContent.setAttribute("aria-modal", "true");

    const titleElement = this.getFirstTitleElement(modalBoxContent);

    if (titleElement) {
      let titleElementId = titleElement.id;

      if (!titleElementId) {
        titleElementId = this._generateUniqueId();
        titleElement.id = titleElementId;
      }
      modalBoxContent.setAttribute("aria-labelledby", titleElementId);
    }
  },
  /**
   * Sets up observation for a specific element to initialize 'initOnFocusLostCloseEvent'
   * when it receives a visibility class.
   * Uses a unique, shared MutationObserver instance.
   *
   * @param {HTMLElement} containerElement - The direct DOM element to observe.
   * @param {HTMLElement} elementToFocusBack - The element to focus back when the popup/container is closed.
   * @param {string} visibilityClass - The class that, when added, indicates that containerElement is "visible".
   * @param {Function} closeCallback - The direct method (function) to call to close the popup/container.
   */
  setupFocusLostObserver(containerElement, elementToFocusBack, visibilityClass, closeCallback) {
    if (!(containerElement instanceof HTMLElement) || containerElement._focusLostObserver) {
      return;
    }
    const observerConfig = {
      attributes: true,
      attributeFilter: ["class"],
    };
    const isContainerVisible = (element) => {
      return element?.classList?.contains(visibilityClass);
    };
    const observerCallback = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "attributes") {
          if (isContainerVisible(containerElement)) {
            this._initOnFocusLostCloseEvent(containerElement, elementToFocusBack, closeCallback, visibilityClass);
            return;
          }
        }
      }
    };
    const observer = new MutationObserver(observerCallback);
    observer.observe(containerElement, observerConfig);
    containerElement._focusLostObserver = observer;
  },
  /**
   * Initializes 'focus sentinel' elements to close a tab/popup when focus is lost.
   * Creates two sentinel elements (one before and one after the container) to detect
   * when focus leaves the container.
   * Manages the addition and removal of sentinels and their listeners.
   *
   * @param {HTMLElement} containerElement - The popup/container element to monitor.
   * @param {HTMLElement} elementToFocusBack - The controller element (button/tab) that opens the popup.
   * @param {Function} closeCallback - Function to call to close the popup.
   */
  _initOnFocusLostCloseEvent(containerElement, elementToFocusBack, closeCallback, visibilityClass) {
    if (!(containerElement instanceof HTMLElement) || !(elementToFocusBack instanceof HTMLElement)) {
      return;
    }

    if (containerElement.dataset.focusLostCloseEventInitialized === "true") {
      return;
    }

    const SENTINEL_CLASS_PREFIX = "bcl-focus-sentinel";

    const createFocusSentinel = (position) => {
      const sentinel = document.createElement("div");
      sentinel.tabIndex = 0;
      sentinel.setAttribute("aria-hidden", "true");
      sentinel.style.cssText = "position:absolute; width:1px; height:1px; overflow:hidden; opacity:0; pointer-events:none;";
      sentinel.className = `${SENTINEL_CLASS_PREFIX}-${position}`;
      return sentinel;
    };

    const prependSentinel = createFocusSentinel("before");
    const appendSentinel = createFocusSentinel("after");

    const closeAndFocusTab = () => {
      if (containerElement.hasAttribute("hidden") || containerElement.style.display === "none") {
        return;
      }
      if (typeof closeCallback === "function") {
        closeCallback();
      }
    };

    const handleFocusExit = (event) => {
      event.preventDefault();
      closeAndFocusTab();
    };

    prependSentinel.addEventListener("focus", handleFocusExit);
    appendSentinel.addEventListener("focus", handleFocusExit);

    const removeSentinels = () => {
      if (prependSentinel.parentNode) {
        prependSentinel.remove();
      }
      if (appendSentinel.parentNode) {
        appendSentinel.remove();
      }
      containerElement.dataset.focusLostCloseEventInitialized = "false";
      if (containerElement._sentinelObserver) {
        containerElement._sentinelObserver.disconnect();
        delete containerElement._sentinelObserver;
      }
    };
    const observerConfig = {
      attributes: true,
      attributeFilter: ["class"],
    };

    const isContainerHidden = (element) => {
      return !element.classList.contains(visibilityClass);
      // return element.classList.contains("hidden") || element.classList.contains("mod--hide") || element.classList.contains("visuallyhidden") || element.classList.contains("mod--hidden");
    };

    const observerCallback = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "attributes") {
          if (isContainerHidden(containerElement)) {
            removeSentinels();
            return;
          }
        }
      }
    };

    const observer = new MutationObserver(observerCallback);
    observer.observe(containerElement, observerConfig);
    containerElement._sentinelObserver = observer;

    containerElement.parentNode.insertBefore(prependSentinel, containerElement);
    containerElement.parentNode.insertBefore(appendSentinel, containerElement.nextSibling);

    containerElement.dataset.focusLostCloseEventInitialized = "true";
    containerElement._removeFocusSentinels = removeSentinels;
  },
  /**
   * Creates a temporary aria-live announcer element, adds a message to it, and
   * destroys it after a reasonable amount of time. This is the most robust way
   * to make ephemeral announcements for screen readers.
   *
   * @param {string} message The text to announce.
   * @param {number} [duration=0] Time in ms before removing the text.
   */
  announce(message, duration = 0) {
    if (!message || typeof message !== "string" || message.trim() === "") {
      return;
    }
    const ANNOUNCER_ID = "announcer-temporal";

    let announcerEl = document.getElementById(ANNOUNCER_ID);

    if (announcerEl) {
      announcerEl.textContent = "";
    } else {
      announcerEl = document.createElement("div");
      announcerEl.id = ANNOUNCER_ID;
      announcerEl.setAttribute("aria-live", "polite");
      announcerEl.setAttribute("aria-atomic", "true");
      announcerEl.className = "visuallyhidden";
      document.body.appendChild(announcerEl);
    }

    requestAnimationFrame(() => {
      announcerEl.textContent = message;
      if (duration > 0) {
        setTimeout(() => {
          if (announcerEl) {
            announcerEl.textContent = "";
          }
        }, duration);
      }
    });
  },
};
